/**
 * Recommendations, roster, and player detail panels (FR-27, FR-30, FR-32, FR-38).
 *
 * The centre column answers "who should I take", the right column answers "what
 * do I still need", and the detail drawer answers "why". Every number the
 * engine produces is reachable from one of the three — FR-27 requires a
 * component breakdown a human can read *and disagree with*, which means showing
 * the weights, not just the result.
 */

import { el, clear, text, fmt } from './dom.ts';
import type { AppState, Store } from '../state/store.ts';
import type { PlayerAvailabilityDetail, PlayerId, Position, Recommendation } from '../core/types.ts';
import type { BlueprintRecommendation } from '../core/blueprint.ts';
import { FANTASY_POSITIONS } from '../core/types.ts';
import { generateTemplateBlurb, type BlurbEvidence } from '../core/blurb.ts';
import { assignRosterSlots } from '../core/valuation.ts';
import { renderScoutingNote } from './scoutingNote.ts';
import { opportunityCost, waitAdvice } from '../core/availability.ts';
import { tierAlerts } from './board.ts';
import { demandForPosition, opponentDemandForState } from './opponentDemand.ts';
import type { OpponentDemandForecast } from '../core/opponentDemand.ts';

// ---------------------------------------------------------------------------
// Top-5 recommendations (FR-27)
// ---------------------------------------------------------------------------

export interface RecommendationRenderOptions {
  /** Closed player IDs are collapsed; all other recommendation explanations are open. */
  collapsedRecommendationIds?: ReadonlySet<PlayerId>;
  onRecommendationExplanationToggle?: (id: PlayerId, expanded: boolean) => void;
}

export function renderRecommendations(
  container: HTMLElement,
  state: AppState,
  onSelect: (id: PlayerId) => void,
  opponentDemand: OpponentDemandForecast | null = opponentDemandForState(state),
  options: RecommendationRenderOptions = {},
): void {
  clear(container);
  if (state.config.active_source_id === 'blueprint') {
    renderBlueprintRecommendations(container, state, onSelect, opponentDemand, options);
    return;
  }
  const engine = state.engine;

  if (!engine || engine.top.length === 0) {
    container.appendChild(el('p', { class: 'empty' }, 'No recommendations yet.'));
    return;
  }

  for (const alert of tierAlerts(state)) {
    container.appendChild(
      el('div', { class: `tier-alert ${alert.urgent ? 'urgent' : ''}` }, alert.message),
    );
  }

  engine.top.forEach((rec, i) => {
    container.appendChild(renderRecommendationCard(rec, i + 1, state, onSelect, opponentDemand, options));
  });
}

function renderBlueprintRecommendations(
  container: HTMLElement,
  state: AppState,
  onSelect: (id: PlayerId) => void,
  opponentDemand: OpponentDemandForecast | null,
  options: RecommendationRenderOptions,
): void {
  const blueprint = state.blueprint;
  if (!blueprint || blueprint.top.length === 0) {
    container.appendChild(
      el(
        'p',
        { class: 'empty' },
        state.blueprintStatus === 'ready'
          ? 'Blueprint has no forecasted players available yet.'
          : 'Blueprint is disabled until a valid forecast snapshot is published.',
      ),
    );
    return;
  }

  for (const alert of tierAlerts(state)) {
    container.appendChild(
      el('div', { class: `tier-alert ${alert.urgent ? 'urgent' : ''}` }, alert.message),
    );
  }

  blueprint.top.forEach((rec, i) => {
    container.appendChild(renderBlueprintRecommendationCard(rec, i + 1, state, onSelect, opponentDemand, options));
  });
}

function renderBlueprintRecommendationCard(
  rec: BlueprintRecommendation,
  position: number,
  state: AppState,
  onSelect: (id: PlayerId) => void,
  opponentDemand: OpponentDemandForecast | null,
  options: RecommendationRenderOptions,
): HTMLElement {
  const v = rec.valuation;
  const bars = el('div', { class: 'breakdown' });
  const components: [string, number, string][] = [
    ['Value', v.contributions.marginal_lineup_value, `Marginal lineup value ${fmt.signed(v.marginal_lineup_value, 1)} over replacement`],
    ['Ranking residual', v.contributions.expert_residual, `Ranking residual ${fmt.signed(v.expert_residual, 2)} versus the model forecast`],
    ['Scarcity', v.contributions.scarcity, 'Dynamic positional scarcity'],
    ['Roster need', v.contributions.roster_need, 'Urgency from your open lineup slots'],
    ['Wait cost', v.contributions.wait_cost, v.availability_known ? `${fmt.pct(v.probability_available_next_pick)} chance to remain at your next pick` : 'Next-pick availability is unknown'],
    ['Upside', v.contributions.upside, `${fmt.pct(v.upside_score)} upside signal`],
    ['Bye', v.contributions.bye_cost, v.bye_cost > 0 ? 'Bye-week conflict' : 'No bye conflict'],
  ];
  const scale = Math.max(...components.map(([, value]) => Math.abs(value)), 0.001);
  for (const [label, value, title] of components) {
    if (value === 0 && label !== 'Value') continue;
    bars.appendChild(
      el(
        'div',
        { class: 'breakdown-row', title },
        el('span', { class: 'breakdown-label' }, label),
        el(
          'span',
          { class: 'breakdown-track' },
          el('span', {
            class: `breakdown-bar ${value < 0 ? 'negative' : 'positive'}`,
            style: { width: `${(Math.abs(value) / scale) * 100}%` },
          }),
        ),
        el('span', { class: 'breakdown-value' }, value.toFixed(3)),
      ),
    );
  }

  const availability = v.availability_known
    ? `${fmt.pct(v.probability_available_next_pick)} to next pick · ${v.expected_regret_if_wait.toFixed(1)} regret if you wait`
    : 'Next-pick availability unknown — no ADP row';

  const expanded = isRecommendationExpanded(rec.player.player_id_sleeper, options);
  const explanation = renderRecommendationExplanation(
    rec.player.player_id_sleeper,
    expanded,
    [
      rec.comparison ? renderBlueprintComparison(rec) : null,
      el('p', { class: 'blueprint-range' }, `Forecast ${v.p10_points.toFixed(0)}–${v.p90_points.toFixed(0)} points · median ${v.p50_points.toFixed(0)} · ${availability}`),
      renderRecommendationDemand(opponentDemand, rec.player.position),
      bars,
    ],
    options,
  );

  return el(
    'article',
    {
      class: `rec-card rec-card-blueprint ${position === 1 ? 'top' : ''} ${expanded ? 'expanded' : ''}`,
      onClick: () => onSelect(rec.player.player_id_sleeper),
      tabindex: '0',
    },
    el(
      'header',
      { class: 'rec-head' },
      el('span', { class: 'rec-rank' }, String(position)),
      el(
        'div',
        { class: 'rec-title' },
        el('span', { class: 'rec-name' }, rec.player.full_name),
        el('span', { class: 'rec-sub' }, `${rec.player.position ?? '—'} · ${rec.player.team ?? 'FA'}${rec.player.bye_week ? ` · bye ${rec.player.bye_week}` : ''}`),
        renderInjuryBadge(state, rec.player.player_id_sleeper),
      ),
      el('span', { class: 'rec-score', title: 'Blueprint composite score' }, v.final_score.toFixed(3)),
    ),
    el('p', { class: 'rec-headline' }, rec.headline),
    explanation,
  );
}

function renderBlueprintComparison(rec: BlueprintRecommendation): HTMLElement {
  const comparison = rec.comparison!;
  const candidateGain = rec.valuation.marginal_lineup_value;
  const scoreText = comparison.tied_at_display_precision
    ? `Tied with ${comparison.compare_to_name} at the displayed Blueprint score`
    : comparison.score_delta > 0
      ? `Leads ${comparison.compare_to_name} by ${comparison.score_delta.toFixed(3)} Blueprint score`
      : `${Math.abs(comparison.score_delta).toFixed(3)} Blueprint score behind ${comparison.compare_to_name}`;
  return el(
    'p',
    { class: 'blueprint-comparison' },
    `${scoreText} · lineup gain ${fmt.signed(candidateGain, 1)} vs ${fmt.signed(comparison.compare_to_lineup_gain, 1)}.`,
  );
}

function renderRecommendationCard(
  rec: Recommendation,
  position: number,
  state: AppState,
  onSelect: (id: PlayerId) => void,
  opponentDemand: OpponentDemandForecast | null,
  options: RecommendationRenderOptions,
): HTMLElement {
  const v = rec.valuation;

  const bars = el('div', { class: 'breakdown' });
  const components: [string, number, string][] = [
    ['Value', v.contributions.vor_z, `VOR ${fmt.signed(v.vor, 0)} vs replacement`],
    ['Ranking', v.contributions.rank_score, `${fmt.pct(v.components.rank_score)} percentile`],
    ['Scarcity', v.contributions.scarcity, `${fmt.pct(v.components.scarcity)} positional pressure`],
    ['Need', v.contributions.need, `${fmt.pct(v.components.need)} roster need`],
    [
      'Availability',
      v.contributions.availability,
      v.availability_known
        ? `${fmt.pct(v.components.availability)} to last to your next pick`
        : 'No ADP for this player — timing is unknown, not zero',
    ],
    ['Bye', v.contributions.bye_penalty, v.bye_penalty > 0 ? 'Bye-week conflict' : 'No bye conflict'],
  ];

  const scale = Math.max(...components.map(([, value]) => Math.abs(value)), 0.001);

  for (const [label, value, title] of components) {
    if (value === 0 && label !== 'Value') continue;
    bars.appendChild(
      el(
        'div',
        { class: 'breakdown-row', title },
        el('span', { class: 'breakdown-label' }, label),
        el(
          'span',
          { class: 'breakdown-track' },
          el('span', {
            class: `breakdown-bar ${value < 0 ? 'negative' : 'positive'}`,
            style: { width: `${(Math.abs(value) / scale) * 100}%` },
          }),
        ),
        el('span', { class: 'breakdown-value' }, value.toFixed(3)),
      ),
    );
  }

  const expanded = isRecommendationExpanded(rec.player.player_id_sleeper, options);
  const explanation = renderRecommendationExplanation(
    rec.player.player_id_sleeper,
    expanded,
    [renderRecommendationDemand(opponentDemand, rec.player.position), bars],
    options,
  );

  return el(
    'article',
    {
      class: `rec-card ${position === 1 ? 'top' : ''} ${expanded ? 'expanded' : ''}`,
      onClick: () => onSelect(rec.player.player_id_sleeper),
      tabindex: '0',
    },
    el(
      'header',
      { class: 'rec-head' },
      el('span', { class: 'rec-rank' }, String(position)),
      el(
        'div',
        { class: 'rec-title' },
        el('span', { class: 'rec-name' }, rec.player.full_name),
        el(
          'span',
          { class: 'rec-sub' },
          `${rec.player.position ?? '—'} · ${rec.player.team ?? 'FA'}${rec.player.bye_week ? ` · bye ${rec.player.bye_week}` : ''}`,
        ),
        renderInjuryBadge(state, rec.player.player_id_sleeper),
      ),
      el('span', { class: 'rec-score', title: 'Composite score' }, v.final_score.toFixed(3)),
    ),
    el('p', { class: 'rec-headline' }, rec.headline),
    explanation,
  );
}

function isRecommendationExpanded(
  playerId: PlayerId,
  options: RecommendationRenderOptions,
): boolean {
  return !options.collapsedRecommendationIds?.has(playerId);
}

function renderRecommendationExplanation(
  playerId: PlayerId,
  expanded: boolean,
  children: Array<HTMLElement | null>,
  options: RecommendationRenderOptions,
): HTMLElement {
  const detailId = `recommendation-explanation-${playerId.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  const toggle = el(
    'button',
    {
      class: 'rec-explain-toggle',
      type: 'button',
      'aria-expanded': String(expanded),
      'aria-controls': detailId,
      onClick: (event: MouseEvent) => {
        event.stopPropagation();
        options.onRecommendationExplanationToggle?.(playerId, !expanded);
      },
    },
    expanded ? 'Hide explanation' : 'Why this player?',
  );
  const details = el(
    'div',
    { class: 'rec-details', id: detailId },
    ...children,
  );
  if (!expanded) details.setAttribute('hidden', '');
  return el('div', { class: 'rec-explanation' }, toggle, details);
}

function renderRecommendationDemand(opponentDemand: OpponentDemandForecast | null, position: Position | null): HTMLElement | null {
  const demand = demandForPosition(opponentDemand, position);
  if (!demand || opponentDemand?.mode !== 'market' || demand.projected_picks === null || !demand.pressure) return null;
  return el(
    'p',
    { class: `rec-demand pressure-${demand.pressure}` },
    `Opponent ${demand.position} demand: ${demand.projected_picks} projected pick${demand.projected_picks === 1 ? '' : 's'} before your turn · ${demand.teams_needing} team${demand.teams_needing === 1 ? '' : 's'} need it.`,
  );
}

function injurySummary(availability: PlayerAvailabilityDetail): string {
  const status = availability.status === 'season_out' ? 'Out for season' : 'Injury warning';
  const reason = availability.reason ? ` · ${availability.reason}` : '';
  const source = availability.source_name ? ` · Source: ${availability.source_name}` : '';
  return `${status}${reason}${source}`;
}

function renderInjuryBadge(state: AppState, playerId: PlayerId): HTMLElement | null {
  const availability = state.playerAvailability.get(playerId);
  if (!availability || availability.status === 'healthy') return null;
  const player = state.universe?.get(playerId);
  return el(
    'span',
    {
      class: `badge injury ${availability.status === 'season_out' ? 'season-out-badge' : ''}`,
      title: injurySummary(availability),
      'aria-label': availability.status === 'season_out' ? 'Out for season' : `Injury: ${player?.injury_status ?? 'reported'}`,
    },
    availability.status === 'season_out' ? 'OUT' : player?.injury_status ?? 'INJ',
  );
}

// ---------------------------------------------------------------------------
// Roster and needs (FR-38)
// ---------------------------------------------------------------------------

/** Human labels for Sleeper's flex slot kinds (`roster_positions` spellings). */
function describeFlexKind(kind: string): string {
  switch (kind) {
    case 'FLEX':
    case 'WRRB_WRT':
      return 'FLEX';
    case 'WRRB_FLEX':
      return 'FLEX (RB/WR)';
    case 'REC_FLEX':
      return 'FLEX (WR/TE)';
    case 'SUPER_FLEX':
      return 'SUPERFLEX';
    default:
      return kind;
  }
}

export interface RosterRenderOptions {
  detailsOpen?: boolean;
  onDetailsToggle?: (open: boolean) => void;
}

export function renderRoster(container: HTMLElement, state: AppState, options: RosterRenderOptions = {}): void {
  clear(container);
  const { engine, universe, draft, league } = state;
  if (!engine || !universe || !draft || !league) return;

  container.appendChild(el('h3', {}, 'Your roster'));

  if (draft.my_draft_slot === null) {
    container.appendChild(
      el(
        'p',
        { class: 'hint' },
        'Your draft slot could not be resolved from Sleeper, so roster-aware advice is off. Everything else still works.',
      ),
    );
  }

  const nameOf = (id: PlayerId): string => {
    const player = universe.get(id);
    if (!player) return id;
    return player.full_name + (player.bye_week ? ` (b${player.bye_week})` : '');
  };

  const assignment = assignRosterSlots(draft.my_roster, league, (id) => universe.get(id)?.position ?? null);

  const needItems = [
    ...Object.entries(engine.need.open_starters)
      .filter(([, count]) => count > 0)
      .map(([position, count]) => `${position} ×${count}`),
    ...engine.need.open_flex
      .filter((flex) => flex.count > 0)
      .map((flex) => `${describeFlexKind(flex.kind)} ×${flex.count}`),
  ];
  const needs = el(
    'section',
    { class: 'roster-needs', 'aria-label': 'Open roster needs' },
    el('h3', {}, 'Open slots'),
    needItems.length > 0
      ? el('div', { class: 'roster-need-chips' }, ...needItems.map((item) => el('span', { class: 'roster-need-chip' }, item)))
      : el('p', { class: 'roster-filled' }, 'Starter and flex slots filled.'),
  );
  container.appendChild(needs);

  // Upcoming picks and runs are the actionable context for deciding whether to
  // wait. Keep it above the named roster so a filled bench never buries it.
  container.appendChild(el('h3', {}, 'Your picks'));
  container.appendChild(
    el(
      'p',
      { class: 'picks-line' },
      draft.my_next_pick_no
        ? `On the clock at #${draft.my_next_pick_no}${draft.my_pick_after_that ? `, then #${draft.my_pick_after_that}` : ''}. Draft is at #${draft.current_pick_no}.`
        : `Draft is at #${draft.current_pick_no}.`,
    ),
  );

  // Positional runs (FR-24) — the thing you want to know before it is obvious.
  if (engine.scarcity.runs.length > 0) {
    const described = engine.scarcity.runs
      .map((pos) => {
        const taken = Math.round((engine.scarcity.recent_rate[pos] ?? 0) * engine.scarcity.window);
        return `${pos} (${taken} of the last ${engine.scarcity.window})`;
      })
      .join(', ');
    container.appendChild(el('p', { class: 'run-warning' }, `Run in progress: ${described}.`));
  }

  const table = el('div', { class: 'roster-table' });
  for (const { position, playerIds } of assignment.starters) {
    const required = league.starters_by_position[position] ?? 0;
    if (required === 0 && playerIds.length === 0) continue;

    const open = engine.need.open_starters[position] ?? 0;
    table.appendChild(
      el(
        'div',
        { class: `roster-line ${open > 0 ? 'needed' : ''}` },
        el('span', { class: 'roster-pos' }, position),
        el('span', { class: 'roster-count' }, `${playerIds.length}/${required || '–'}`),
        el('span', { class: 'roster-names' }, playerIds.map(nameOf).join(', ') || '—'),
      ),
    );
  }
  for (const { kind, playerIds } of assignment.flex) {
    const total = league.flex_slots.find((f) => f.kind === kind)?.count ?? playerIds.length;
    table.appendChild(
      el(
        'div',
        { class: 'roster-line' },
        el('span', { class: 'roster-pos' }, describeFlexKind(kind)),
        el('span', { class: 'roster-count' }, `${playerIds.length}/${total}`),
        el('span', { class: 'roster-names' }, playerIds.map(nameOf).join(', ') || '—'),
      ),
    );
  }
  if (league.bench_size > 0 || assignment.bench.length > 0) {
    table.appendChild(
      el(
        'div',
        { class: 'roster-line' },
        el('span', { class: 'roster-pos' }, 'BN'),
        el('span', { class: 'roster-count' }, `${assignment.bench.length}/${league.bench_size || '–'}`),
        el('span', { class: 'roster-names' }, assignment.bench.map(nameOf).join(', ') || '—'),
      ),
    );
  }
  const totalSlots = Object.values(league.starters_by_position).reduce((sum, count) => sum + count, 0)
    + league.flex_slots.reduce((sum, flex) => sum + flex.count, 0)
    + league.bench_size;
  const rosterDetails = el(
    'details',
    { class: 'roster-details', open: options.detailsOpen === true },
    el('summary', { class: 'roster-details-toggle' }, `Full roster (${draft.my_roster.length}/${totalSlots})`),
    table,
  );
  rosterDetails.addEventListener('toggle', () => options.onDetailsToggle?.(rosterDetails.open));
  container.appendChild(rosterDetails);
}

// ---------------------------------------------------------------------------
// Player detail (FR-13, FR-14, FR-30, FR-32)
// ---------------------------------------------------------------------------

export function renderPlayerDetail(
  container: HTMLElement,
  state: AppState,
  store: Store,
  playerId: PlayerId,
  onClose: () => void,
): void {
  clear(container);
  const { universe, engine, blueprint, blend, comparison, league, draft, adpIndex } = state;
  const player = universe?.get(playerId);
  if (!player || !engine || !blend || !league || !draft) return;

  const valuation = engine.valuations.get(playerId);
  const view = blend.byPlayer.get(playerId);
  // FR-13: compare across every loaded source, not just the active one.
  const compareView = (comparison ?? blend).byPlayer.get(playerId);
  const adp = adpIndex.get(playerId);
  const localOverride = state.availabilityOverrides.get(playerId);
  const drafted = localOverride ? undefined : draft.picks.find((p) => p.player_id === playerId);

  container.appendChild(
    el(
      'header',
      { class: 'detail-head' },
      el(
        'div',
        {},
        el('h2', {}, player.full_name),
        el(
          'p',
          { class: 'detail-sub' },
          `${player.position ?? '—'} · ${player.team ?? 'FA'}${player.bye_week ? ` · bye ${player.bye_week}` : ''}${player.injury_status ? ` · ${player.injury_status}` : ''}`,
        ),
      ),
      el('button', { class: 'btn btn-quiet', type: 'button', onClick: onClose, 'aria-label': 'Close' }, '×'),
    ),
  );

  const availability = state.playerAvailability.get(playerId);
  if (availability && availability.status !== 'healthy') {
    const source = availability.source_url
      ? el('a', { href: availability.source_url, target: '_blank', rel: 'noopener noreferrer' }, availability.source_name ?? 'Source')
      : null;
    container.appendChild(
      el(
        'section',
        { class: `detail-injury ${availability.status === 'season_out' ? 'season-out' : ''}`, 'aria-label': 'Injury status' },
        el('h3', {}, availability.status === 'season_out' ? 'Out for season' : 'Injury status'),
        el('p', {}, availability.reason ?? 'Sleeper reports an injury-related status.'),
        availability.status === 'season_out'
          ? el('p', { class: 'hint' }, league.league_type === 'dynasty'
            ? 'Dynasty mode keeps him eligible because this designation applies only to the current season.'
            : 'Removed from current-season recommendations; use Showing all to inspect him.')
          : null,
        source ? el('p', { class: 'hint' }, 'Report: ', source) : null,
      ),
    );
  }

  // General scouting context: static, researched ahead of time, and dated
  // rather than generated on demand — deliberately separate from "The case"
  // below, which is computed live from this team's own roster and pick. Not
  // every player has one yet (NOTES.md §23 covers the top ~300 and counting).
  //
  // The notes are ~157kb and are the single largest thing the app ships, so
  // they load in their own chunk on first panel open rather than sitting in
  // the bundle the board's first paint waits on (NOTES.md §29). The slot is
  // reserved synchronously so the section lands here rather than at the bottom
  // of the panel whenever the fetch finishes.
  const noteHost = el('div', { class: 'scouting-note' });
  container.appendChild(noteHost);
  renderScoutingNote(noteHost, playerId);

  const blueprintValuation = blueprint?.valuations.get(playerId);
  if (state.config.active_source_id === 'blueprint' && blueprintValuation) {
    const b = blueprintValuation;
    container.appendChild(el('h3', {}, 'Blueprint forecast'));
    container.appendChild(
      el(
        'div',
        { class: 'blueprint-detail' },
        el('p', { class: 'blueprint-range' }, `P10 ${b.p10_points.toFixed(0)} · P50 ${b.p50_points.toFixed(0)} · P90 ${b.p90_points.toFixed(0)} points`),
        el('p', {}, `Model ${blueprint!.snapshot.model_version}; ${b.expected_games_active.toFixed(1)} expected games active.`),
        el('p', {}, `Marginal lineup gain ${fmt.signed(b.marginal_lineup_value, 1)} · ${fmt.pct(b.probability_available_next_pick)} available at your next pick${b.availability_known ? ` · ${b.expected_regret_if_wait.toFixed(1)} expected regret if you wait` : ''}.`),
        b.risk_flags.length
          ? el('p', { class: 'hint' }, `Risks: ${b.risk_flags.join(' ')}`)
          : el('p', { class: 'hint' }, 'No additional risk flags in this snapshot.'),
      ),
    );
  }

  // FR-13: every source side by side. FR-14: the disagreement score.
  if (compareView) {
    const sourceList = el('div', { class: 'source-compare' });
    for (const rank of compareView.ranks) {
      sourceList.appendChild(
        el(
          'div',
          { class: `source-rank ${rank.imputed ? 'imputed' : ''}` },
          el('span', { class: 'source-name' }, rank.source_name),
          el('span', { class: 'source-value' }, rank.imputed ? `>${rank.overall_rank - 1}` : String(rank.overall_rank)),
          rank.imputed ? el('span', { class: 'source-flag', title: 'Not on this list — imputed' }, 'imputed') : null,
        ),
      );
    }
    container.appendChild(el('h3', {}, 'Across your sources'));
    container.appendChild(sourceList);
    if (compareView.ranks.length < 2) {
      container.appendChild(
        el(
          'p',
          { class: 'hint' },
          'Only one ranking source is loaded, so there is nothing to compare him against yet. "+ Rankings" adds another.',
        ),
      );
    } else if (compareView.disagreement > 0) {
      container.appendChild(
        el(
          'p',
          { class: 'disagreement' },
          `Disagreement ${compareView.disagreement.toFixed(1)} of 100 — ${compareView.disagreement > 15 ? 'your sources genuinely differ on him' : 'your sources broadly agree'}. It is the spread between where your sources place him, measured on percentiles so lists of different lengths stay comparable.`,
        ),
      );
    }
  }

  // FR-30: wait vs. take now, plus the opportunity cost comparison.
  if (valuation && !drafted) {
    const advice = waitAdvice(
      player.full_name,
      adp,
      draft,
      league.teams,
      league.rounds,
      league.draft_type,
    );
    container.appendChild(el('h3', {}, 'Wait, or take him now?'));
    container.appendChild(el('p', { class: `wait-advice ${advice.verdict}` }, advice.sentence));

    if (player.position && valuation) {
      const samePosition = [...engine.valuations.values()]
        .filter((other) => universe?.get(other.player_key)?.position === player.position)
        .map((other) => ({
          player_key: other.player_key,
          name: universe!.get(other.player_key)!.full_name,
          points: other.proj_points,
          adp: adpIndex.get(other.player_key),
        }));

      const cost = opportunityCost(
        { player_key: playerId, name: player.full_name, points: valuation.proj_points },
        samePosition,
        draft,
        league.teams,
        league.rounds,
        league.draft_type,
      );

      if (cost.fallbacks.length > 0) {
        container.appendChild(
          el(
            'p',
            { class: 'opportunity' },
            `Taking him now is worth about ${fmt.signed(cost.delta, 1)} points against the best ${player.position} you would expect at your next turn (${cost.expected_if_wait.toFixed(1)} projected). Likely fallbacks: ${cost.fallbacks.map((f) => `${f.name} ${fmt.pct(f.probability)}`).join(', ')}.`,
          ),
        );
      }
    }
  }

  // FR-32/FR-36: the deterministic blurb, labelled as generated.
  if (valuation) {
    const evidence: BlurbEvidence = {
      player,
      valuation,
      rankView: view,
      comparison: compareView,
      adp,
      wait: waitAdvice(player.full_name, adp, draft, league.teams, league.rounds, league.draft_type),
      scarcity: engine.scarcity,
      need: engine.need,
      league,
      draft,
      tier: engine.tiers.get(playerId),
      notes: view?.ranks.length ? null : null,
    };
    // The "Wait, or take him now?" section above already prints the timing
    // sentence for an undrafted player, so let the case skip it rather than
    // show the identical sentence twice on one screen.
    const blurb = generateTemplateBlurb(evidence, { omitTiming: !drafted });

    container.appendChild(el('h3', {}, 'The case', el('span', { class: 'badge generated' }, 'generated')));
    const caseList = el('div', { class: 'blurb' });
    for (const sentence of blurb.case) caseList.appendChild(el('p', {}, sentence));
    container.appendChild(caseList);

    if (blurb.risks.length > 0) {
      const risks = el('ul', { class: 'risks' });
      for (const risk of blurb.risks) risks.appendChild(el('li', {}, risk));
      container.appendChild(el('h3', {}, 'Risks'));
      container.appendChild(risks);
    }
  }

  // FR-12: manual override, right where the user is already looking.
  container.appendChild(
    el(
      'div',
      { class: 'detail-actions' },
      localOverride
        ? el(
            'button',
            { class: 'btn', type: 'button', onClick: () => store.markDrafted(playerId) },
            'Restore Sleeper status',
          )
        : drafted
        ? el(
            'button',
            { class: 'btn', type: 'button', onClick: () => store.markUndrafted(playerId) },
            'Mark as available',
          )
        : el(
            'button',
            { class: 'btn', type: 'button', onClick: () => store.markDrafted(playerId) },
            'Mark as drafted',
          ),
    ),
  );

  if (drafted) {
    container.appendChild(
      el(
        'p',
        { class: 'hint' },
        `Drafted at pick #${drafted.pick_no} by ${drafted.team_name ?? 'unknown'}${drafted.manual ? ' (entered by hand)' : ''}.`,
      ),
    );
  }
  if (localOverride) {
    container.appendChild(
      el(
        'p',
        { class: 'hint' },
        `Sleeper reports this player at pick #${localOverride.pick_no}, but this device is treating him as available.`,
      ),
    );
  }
}
